'use client';
import {useEffect,useRef} from "react";
import {Card,Stack,Text,Title,Badge} from "@mantine/core";
import {useLocale} from "../lib/i18n/use_locale";
import {useGamepad} from "./use-gamepad";
import {browserGamepad} from "../lib/services/input/browser-gamepad";
import {gamepadMenu,clearGamepadFocus} from "../lib/services/input/gamepad-menu";
import {gamepadPrompts} from "../lib/services/input/gamepad-prompts";

export default function GamepadSettings(){
  const pad=useGamepad(),zh=useLocale()==="zh",root=useRef<HTMLDivElement>(null);
  const prompts=gamepadPrompts(pad.id);
  useEffect(()=>{
    let using=false;
    return browserGamepad.onFrame(frame=>{
      const main=root.current?.closest("main");
      if(main&&frame.standard&&frame.armed)gamepadMenu(main,frame);
      if(main&&using&&!frame.usingGamepad)clearGamepadFocus(main);
      using=frame.usingGamepad;
    });
  },[]);
  const status=!pad.available?(zh?"此浏览器不支持手柄":"Gamepad API unavailable"):pad.error?
    (zh?"手柄访问受限，请使用 HTTPS 并检查浏览器权限":"Controller access blocked. Use HTTPS and check browser permissions."):
    !pad.connected?(zh?"连接手柄后按任意按钮":"Connect a controller, then press a button"):
    !pad.standard?(zh?"非标准映射：仅显示诊断数据":"Non-standard mapping: diagnostic input only"):
    !pad.armed?(zh?"松开摇杆和按钮以启用":"Release sticks and buttons to enable"):
    (zh?"手柄已就绪":"Controller ready");
  return <Card ref={root} radius="md" padding="lg" style={{background:"#23262f",border:"1px solid #ffffff10",color:"#ebe6e6"}}
    data-gamepad-diagnostic>
    <style>{'[data-gamepad-focus]{outline:3px solid #ffbf69!important;outline-offset:4px;border-radius:5px}'}</style>
    <Stack gap="sm">
      <Title order={3} size="h4">{zh?"游戏手柄":"Game controller"}</Title>
      <Text size="sm" role="status" aria-live="polite">{status}</Text>
      {pad.connected&&<>
        <Text size="xs" style={{overflowWrap:"anywhere"}}>{pad.id}</Text>
        <Text size="xs" c="#b8b8b8">{zh?"映射":"Mapping"}: {pad.standard?"standard":zh?"自定义":"custom"} · {zh?"编号":"Index"} {pad.index}</Text>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <div role="img" aria-label={`${zh?"左摇杆":"Left stick"} X ${(pad.axes[0]??0).toFixed(2)}, Y ${(pad.axes[1]??0).toFixed(2)}`}
            style={{width:76,height:76,flexShrink:0,border:"2px solid #b8b8b8",borderRadius:"50%",position:"relative"}}>
            <span style={{position:"absolute",width:16,height:16,borderRadius:"50%",background:"#ffb45b",
              left:`${50+(pad.axes[0]??0)*36}%`,top:`${50+(pad.axes[1]??0)*36}%`,transform:"translate(-50%,-50%)"}}/>
          </div>
          <Text size="xs" c="#b8b8b8" style={{fontVariantNumeric:"tabular-nums",overflowWrap:"anywhere"}}>
            {zh?"轴":"Axes"}: {pad.axes.map((v,i)=>`${i}: ${v.toFixed(2)}`).join(" · ")}
          </Text>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}} aria-label={zh?"按钮实时值":"Live button values"}>
          {pad.buttons.map((value,i)=><Badge key={i} color={value>.4?"orange":"gray"} variant={value>.4?"filled":"outline"}>
            {i}: {value.toFixed(2)}
          </Badge>)}
        </div>
      </>}
      <Text size="sm">{zh?`左摇杆：移动 / 奔跑 · ${prompts.confirm}：拾取 / 交换 · ${prompts.weapon}：武器 · ${prompts.power}：内置技能`:
        `Left stick: move / run · ${prompts.confirm}: pick up / swap · ${prompts.weapon}: weapon · ${prompts.power}: built-in power`}</Text>
      <Text size="xs" c="#b8b8b8">{zh?
        `方向键：菜单 · ${prompts.confirm}：确认 · ${prompts.back}：返回 · ${prompts.menu}：暂停。${prompts.confirm} / ${prompts.back} 指底部 / 右侧按钮，${prompts.weapon} / ${prompts.power} 指右 / 左扳机。`:
        `D-pad: menus · ${prompts.confirm}: confirm · ${prompts.back}: back · ${prompts.menu}: pause. ${prompts.confirm} / ${prompts.back} mean the bottom / right face buttons; ${prompts.weapon} / ${prompts.power} mean right / left triggers.`}</Text>
      <Text size="xs" c="#b8b8b8">{zh?"触屏和键盘始终可用。首次播放声音时，浏览器可能需要点击或轻触页面。":"Touch and keyboard remain available. Your browser may require a click or tap on the page to enable sound."}</Text>
    </Stack>
  </Card>;
}
